import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';
import React from 'react';
import 'swiper/css';
import { FreeMode } from 'swiper/modules';
import {
  Swiper as ReactSwiper,
  SwiperSlide as ReactSwiperSlide,
} from 'swiper/react';
import { UrlObject } from 'url';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import theme from '@/styles/theme';

export type TabItemType = {
  key: string;
  label: React.ReactNode;
  badgeCount?: number;
  href: UrlObject | string;
  target?: '_blank' | '_self';
};

type TabButtonProps = {
  className?: string;
  items: TabItemType[];
  hasInnerArea?: boolean;
  scroll?: boolean;
  onChange?: (activeKey: string) => void;
};

export default function TabButton({
  className,
  items,
  hasInnerArea = false,
  scroll = true,
  onChange,
}: TabButtonProps) {
  const router = useRouter();
  const activeKey = String(router.query.subcategory) || '';

  const isMobile = useMediaQuery({
    query: `(max-width: ${theme`screens.lg`})`,
    defaultMatches: true,
  });

  return (
    <div
      className={`flex w-full items-center justify-center gap-2 [&::-webkit-scrollbar]:hidden md:gap-2.5 md:text-base ${className ?? ''}`}
    >
      <ReactSwiper
        className={`m-0 w-screen flex-1 tab-button-swiper ${isMobile ? 'tab-button-swiper--mobile' : ''} ${hasInnerArea ? 'px-gutter max-w-[1520px]' : ''}`}
        key={activeKey}
        modules={[FreeMode]}
        slidesPerView="auto"
        freeMode
        touchRatio={isMobile ? 1 : 0}
        grabCursor
        spaceBetween={0}
        scrollbar={{ draggable: true }}
        initialSlide={
          activeKey && isMobile
            ? items.findIndex((item) => item.key === activeKey)
            : 0
        }
        // 슬라이드 마지막에 'SwiperSlide 개수+1 * gap 너비' 여백
        slidesOffsetAfter={(items.length + 1) * 8}
      >
        {items.map((item) => {
          if (item.href) {
            return (
              <ReactSwiperSlide className="w-fit" key={item.key}>
                <Link
                  href={item.href}
                  scroll={scroll}
                  onClick={() => onChange?.(item.key)}
                >
                  <span className={tabButtonItemClass(item.key === activeKey)}>
                    {item.label}
                  </span>
                </Link>
              </ReactSwiperSlide>
            );
          } else {
            return (
              <ReactSwiperSlide className="w-fit" key={item.key}>
                <button
                  type="button"
                  className={tabButtonItemClass(item.key === activeKey)}
                  onClick={() => onChange?.(item.key)}
                >
                  {item.label}
                </button>
              </ReactSwiperSlide>
            );
          }
        })}
      </ReactSwiper>
    </div>
  );
}

/** active 는 색·테두리·배경을 모두 바꾸므로 상태별로 한 벌씩만 붙인다 */
function tabButtonItemClass(isActive: boolean) {
  return [
    'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-[50px] border border-solid px-4 py-2.5 text-sm font-medium leading-[160%] md:px-5 md:py-3 md:text-[15px]',
    isActive
      ? 'border-apps-corePrimary01 bg-apps-corePrimary05 text-apps-corePrimary01'
      : 'border-apps-coreProductBorder text-apps-coreGray01',
  ].join(' ');
}
